import { useState, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { validationSchemaManager } from './validationSchemaManager.ts';
import { useNavigate } from 'react-router';
import { ManagerFormContext } from './CreateFormManagerContext.tsx';
import { SuccessManagerModal } from '~/modules/processing-personal/PersonalDataForm/CreateFormManager/SuccessManagerModal/SuccessManagerModal.tsx';
import {
  TFormValues,
  TFinancialServicesValue,
  TBusinessRelationshipValue,
  TSubmitData,
  TErrorResponse,
} from '../types.ts';
import { initialFormValues } from '../constants.ts';
import { useAppDispatch } from '~/store/appDispatch.ts';
import { contrAgentType } from '~/constants/directoryList.ts';
import { createApplicationPersonalDataManager } from '~/store/processing-personal/thunks/personalDataApplicationsThunks.ts';
import { ERROR_CODES } from '~/constants/errorCodes.js';
import { errorMessages } from '~/constants/errorMessages.js';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import { styles } from '~/modules/styles.ts';
import { Box } from '@mui/material';
import useGetUserRole from '~/hooks/useGetUserRole.ts';

const PersonalDataCreateApplicationByManager = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isManagerLogin, roles } = useGetUserRole();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [publicLink, setPublicLink] = useState<string>('');
  const [isOpenModalAlert, setIsOpenModalAlert] = useState<boolean>(false);
  const [modalAlertText, setModalAlertText] = useState<string>('');

  const form = useForm<TFormValues>({
    defaultValues: initialFormValues,
    resolver: yupResolver(validationSchemaManager),
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  const handleModalClose = (): void => {
    setIsModalOpen(false);
  };

  const closeModalAlert = (): void => setIsOpenModalAlert(false);
  const handleBack = (): void => {
    navigate('/personal-data-table');
  };

  useEffect(() => {
    if (!isManagerLogin && !roles) {
      navigate('/services');
    }
  }, [isManagerLogin, navigate, roles]);

  const onSubmit: SubmitHandler<TFormValues> = async (values) => {
    const financialServicesValue: TFinancialServicesValue = {
      selected: values.financialServices,
      ...(values.financialServices &&
        values.financialServicesClientType && {
          clientType: values.financialServicesClientType.value as string,
        }),
    };

    const businessRelationshipValue: TBusinessRelationshipValue = {
      selected: values.businessRelationship,
      ...(values.businessRelationship && {
        clientType: contrAgentType[1]?.value as string,
      }),
    };

    const data: TSubmitData = {
      financialServices: financialServicesValue,
      cardAccess: { selected: values.cardAccess },
      businessRelationship: businessRelationshipValue,
      creditBureauData: { selected: values.creditBureauData },
      email: values.email,
      phone: values.phone,
    };

    try {
      const res = await dispatch(createApplicationPersonalDataManager({ data })).unwrap();

      if (res?.publicLink) {
        setIsModalOpen(true);
        setPublicLink(res.publicLink);
      } else {
        setIsModalOpen(false);
      }
    } catch (error) {
      const err = error as TErrorResponse;
      const errorMessage = err?.data?.message ?? errorMessages.SERVICE_UNAVAILABLE;
      if (Number(err?.data?.errorCode) === ERROR_CODES.INVALID_ARGUMENTS) {
        setIsOpenModalAlert(true);
        setModalAlertText(errorMessage);
      } else {
        setIsOpenModalAlert(true);
        setModalAlertText(errorMessages.SERVICE_UNAVAILABLE);
      }
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Box sx={styles.pageContainer}>
          <Box sx={styles.formWrapper}>
            <ManagerFormContext />
          </Box>
        </Box>

        <SuccessManagerModal
          isOpen={isModalOpen}
          publicLink={publicLink}
          onClose={handleModalClose}
          onComplete={handleBack}
        />

        {isOpenModalAlert && (
          <ModalAlert isOpen={isOpenModalAlert} text={modalAlertText} onClose={closeModalAlert} />
        )}
      </form>
    </FormProvider>
  );
};

export default PersonalDataCreateApplicationByManager;
